import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { useRef } from 'react';
import { useSelector } from 'react-redux';

// material-ui
import {
  Card,
  CardContent,
  Grid,
  makeStyles,
  Box,
  Typography,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';

// icons
import { Delete, Email, Refresh } from '@material-ui/icons';

// components
import Header from 'components/Header';
import Button from 'components/Button';
import IconButton from 'components/IconButton';
import Toast from 'components/Toast';

// api
import {
  useEmailIntegrationsQuery,
  useConnectGmailMutation,
  useDeleteEmailIntegrationMutation,
  useReconnectGmailMutation,
} from 'api/emailIntegrations';

// selectors
import getCurrentUser from 'store/selectors/getCurrentUser';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GMAIL_AUTH_SCOPE = 'https://www.googleapis.com/auth/gmail.send'; // We only need to send emails, not read or store drafts at this time

const EmailIntegrationItem = ({
  integration,
  onDelete,
  onReconnect,
  isDeleting,
  isReconnecting,
}: {
  integration: { id: number; email: string };
  onDelete: () => void;
  onReconnect: () => void;
  isDeleting: boolean;
  isReconnecting: boolean;
}) => (
  <Box display='flex' alignItems='center' justifyContent='space-between'>
    <Typography variant='body2'>{integration.email}</Typography>
    <Box display='flex' alignItems='center'>
      <Tooltip title='Reconnect Gmail account'>
        <span>
          <IconButton
            id={`reconnectEmailIntegration-${integration.id}`}
            size='small'
            onClick={onReconnect}
            loading={isReconnecting}
          >
            <Refresh fontSize='small' />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title='Disconnect Gmail account'>
        <span>
          <IconButton
            id={`deleteEmailIntegration-${integration.id}`}
            size='small'
            onClick={onDelete}
            loading={isDeleting}
          >
            <Delete fontSize='small' />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  </Box>
);

const EmailIntegrationsGoogle = () => {
  const classes = useStyles();
  const accountId = useSelector(getCurrentUser).get('id');

  // Track which integration id is pending reconnect so the auth-code callback can use it
  const reconnectingIdRef = useRef<number | null>(null);

  const {
    data: emailIntegrations,
    isLoading: emailIntegrationsLoading,
  } = useEmailIntegrationsQuery();

  const {
    mutateAsync: connectGmail,
    isLoading: connectGmailLoading,
    isError: connectGmailError,
    isSuccess: connectGmailSuccess,
    error: connectGmailErrorResponse,
  } = useConnectGmailMutation();

  const {
    mutate: deleteEmailIntegration,
    isLoading: deleteLoading,
    isError: deleteError,
    isSuccess: deleteSuccess,
    error: deleteErrorResponse,
    variables: deleteVariables,
  } = useDeleteEmailIntegrationMutation();

  const {
    mutate: reconnectGmail,
    isLoading: reconnectLoading,
    isError: reconnectError,
    isSuccess: reconnectSuccess,
    error: reconnectErrorResponse,
    variables: reconnectVariables,
  } = useReconnectGmailMutation();

  const loginGoogle = useGoogleLogin({
    flow: 'auth-code',
    scope: GMAIL_AUTH_SCOPE,
    // redirect_uri is 'postmessage' when exchanging on the server side per Google's recommended pattern for SPA->backend exchange
    onSuccess: ({ code }) => {
      connectGmail(code);
    },
  });

  const reconnectGoogle = useGoogleLogin({
    flow: 'auth-code',
    scope: GMAIL_AUTH_SCOPE,
    onSuccess: ({ code }) => {
      const integrationId = reconnectingIdRef.current;
      reconnectingIdRef.current = null;
      if (integrationId !== null) {
        reconnectGmail({ integrationId, authCode: code });
      }
    },
  });

  const handleReconnect = (integrationId: number) => {
    reconnectingIdRef.current = integrationId;
    reconnectGoogle();
  };

  return (
    <Box width='100%'>
      <Box display='flex' justifyContent='center'>
        <Button
          color='primary'
          variant='contained'
          size='large'
          className={classes.button}
          disabled={!accountId}
          loading={connectGmailLoading}
          onClick={() => loginGoogle()}
        >
          Connect Gmail
        </Button>
      </Box>

      <Box mt={2} width='70%' mx='auto'>
        {emailIntegrationsLoading && (
          <Box display='flex' justifyContent='center'>
            <CircularProgress size={24} />
          </Box>
        )}

        {!emailIntegrationsLoading && (emailIntegrations ?? []).length === 0 && (
          <Typography variant='body2' color='textSecondary' align='center'>
            No email integrations connected.
          </Typography>
        )}

        {(emailIntegrations ?? []).length > 0 && (
          <Box>
            <Typography variant='subtitle2' gutterBottom>
              Connected Accounts
            </Typography>
            {(emailIntegrations ?? []).map(integration => (
              <EmailIntegrationItem
                key={integration.id}
                integration={integration}
                onDelete={() => deleteEmailIntegration(integration.id)}
                onReconnect={() => handleReconnect(integration.id)}
                isDeleting={deleteLoading && deleteVariables === integration.id}
                isReconnecting={
                  reconnectLoading &&
                  reconnectVariables?.integrationId === integration.id
                }
              />
            ))}
          </Box>
        )}
      </Box>

      <Toast
        id='emailIntegrationToast'
        isError={connectGmailError}
        isSuccess={connectGmailSuccess}
        successMessage='Email connected successfully.'
        failureMessage={
          (connectGmailErrorResponse?.response?.data as any)?.msg ||
          'Failed to connect email.'
        }
        snackbarProps={{ autoHideDuration: 4000 }}
      />

      <Toast
        id='deleteEmailIntegrationToast'
        isError={deleteError}
        isSuccess={deleteSuccess}
        successMessage='Email integration removed.'
        failureMessage={
          (deleteErrorResponse?.response?.data as any)?.msg ||
          'Failed to remove email integration.'
        }
        snackbarProps={{ autoHideDuration: 4000 }}
      />

      <Toast
        id='reconnectEmailIntegrationToast'
        isError={reconnectError}
        isSuccess={reconnectSuccess}
        successMessage='Gmail account reconnected successfully.'
        failureMessage={
          (reconnectErrorResponse?.response?.data as any)?.msg ||
          'Failed to reconnect Gmail account.'
        }
        snackbarProps={{ autoHideDuration: 4000 }}
      />
    </Box>
  );
};

const EmailIntegrationsCard = () => (
  <Grid item sm={12} md={6}>
    <Card id='emailIntegrationsContent'>
      <CardContent>
        <Header
          id='header'
          icon={Email}
          title='Personal Email Integrations'
          subtitle='Connect a personal email account to ChapterBuilder to send emails to your potential members from.'
        />

        <Box display='flex' justifyContent='center'>
          <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <EmailIntegrationsGoogle />
          </GoogleOAuthProvider>
        </Box>
      </CardContent>
    </Card>
  </Grid>
);

const useStyles = makeStyles(() => {
  return {
    button: {
      width: '70%',
    },
  };
});

export default EmailIntegrationsCard;
